import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlString, useUrlRangeOrNull } from '../../hooks/useUrlState';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Card, Tag, Typography, Select, Button, Tooltip, theme, Input, DatePicker, Space, Row, Col, Popconfirm, Popover, message, Segmented } from '../../ui';
import { DollarSign, Plus, Wallet, Clock, CheckCircle2, XCircle, Link, Pencil, Eye, Landmark, Info, ArrowRight, Download, HandCoins } from 'lucide-react';
import { StatCard, StatTone } from '../../ui/StatCard';
import { AttachmentCell } from '../../components/AttachmentPreview';
import { BankCollectionsView } from './BankCollectionsView';
import dayjs, { Dayjs } from 'dayjs';
import { disableFuture } from '../../lib/dateRange';
import { request } from '../../api/client';
import { useFocusRow } from '../../hooks/useFocusRow';
import { usePermission } from '../../store/auth';
import { TwDataTable as DataTable, DataTableColumn } from '../../components/TwDataTable';
import { RetailerLink, UserLink } from '../../components/EntityLinks';
import { ExportDialog } from '../../components/ExportDialog';
import { useDebounce } from '../../hooks/useDebounce';
import { useRetailerSearch } from '../../hooks/useRetailerSearch';
import { useUrlTab } from '../../hooks/useUrlTab';
import { useServerSort } from '../../hooks/useServerSort';
import { formatIst, formatIstDate, formatIstTime, toIstDateString } from '../../lib/time';
import { PaymentFormModal, bankAcctLabel } from './PaymentFormModal';
import { EditPaymentModal, EditablePayment } from './EditPaymentModal';
import { PaymentReviewModal } from './PaymentReviewModal';
import { inr2 } from '../../lib/format';
import { customerOption, CUSTOMER_SELECT_PROPS } from '../../components/CustomerOption';
import { inr as inrWhole } from '../../lib/format';

// The shared formatter: a negative reads "-₹480", never "₹-480".
const inr = inrWhole;
/** Roles that collect money in the field, as opposed to recording it at a desk. */
const FIELD_ROLES = ['SALES_PERSON', 'SALES_HEAD'];

/**
 * What this payment DID to its order, in words.
 *
 * "₹8,249 was collected" is only half a fact — the half that decides anything is whether the order
 * is now clear or still carries a balance. A pending claim has settled nothing yet, so it is phrased
 * conditionally ("if approved…"): saying "part payment" of money nobody has verified would state a
 * settlement that has not happened.
 */
export function orderCoverage(p: any): { text: string; tone: 'done' | 'part' | 'idle' } | null {
  if (!p?.orderId || p.orderTotal == null) return null;
  const total = Number(p.orderTotal) || 0;
  const paid = Number(p.orderPaid) || 0;
  const amount = Number(p.amount) || 0;
  if (total <= 0) return null;
  if (p.approvalStatus === 'REJECTED') return { text: 'rejected — not counted', tone: 'idle' };
  if (p.approvalStatus === 'PENDING') {
    const after = paid + amount;
    return after >= total - 0.009
      ? { text: 'if approved, clears the order', tone: 'part' }
      : { text: `if approved, ${inr(total - after)} still due`, tone: 'part' };
  }
  const due = total - paid;
  return due <= 0.009
    ? { text: 'order fully paid', tone: 'done' }
    : { text: `part payment · ${inr(due)} still due`, tone: 'part' };
}

/**
 * Who brought the money in, as an ACT rather than a name: a rep collecting in the field and a
 * retailer paying from their own login are different events, and the register showed both as a name
 * beside a role pill, leaving the reader to infer which had happened.
 */
export function submissionKind(p: any): string | null {
  /* A credit APPLICATION is not a collection by anybody. No money moved: the retailer's own account
     credit was applied to an order, and whoever keyed it in did not receive a rupee. Saying "Asmita
     collected this from the retailer" against a Credit row states an event that cannot happen. */
  if (p?.fromCredit) return 'applied from the retailer’s account credit — nobody collected money';
  const code = p?.submittedByRoleCode ?? (p?.submittedByRole === 'Retailer' ? 'RETAILER' : undefined);
  if (code === 'RETAILER') return 'paid from their own login';
  if (FIELD_ROLES.includes(String(code))) {
    /*
     * Two different field events, and only one of them puts money in anybody's hands.
     *
     *  · The common one: the rep stands with the retailer, the retailer pays by UPI or transfer
     *    into a company account, and sends the rep the screenshot because they are not going to key
     *    it in themselves. The rep RECORDS it on their behalf — nothing was collected, the money
     *    went straight to the bank.
     *  · The other one: the retailer hands over notes. Now the rep is carrying company cash, and
     *    that becomes custody they have to hand over (see rep_cash_custody).
     *
     * Calling both "collected" made the first look like cash in a pocket that was never there.
     */
    const isCash = String(p?.mode ?? '').toUpperCase() === 'CASH' && !p?.receivedBank;
    return isCash ? 'collected cash from the retailer' : 'recorded on behalf of the retailer';
  }
  return null;   // recorded at a desk by Accounts/Admin — the role pill already says so
}

const APPROVAL: Record<string, { color: string; label: string }> = {
  PENDING: { color: 'gold', label: 'Pending approval' },
  APPROVED: { color: 'green', label: 'Approved' },
  REJECTED: { color: 'red', label: 'Rejected' },
};
const MODES = ['CASH', 'CHEQUE', 'UPI', 'NEFT', 'RTGS', 'IMPS', 'CARD', 'OTHER'];

/**
 * Payments are order-based now (invoice allocation retired). Retailer/SP/SH submissions arrive
 * PENDING; Accounts/Admin/SA approve (posts the ledger credit) or reject. Staff can also record a
 * payment directly (auto-approved) via "Record Payment".
 */
export function PaymentsListPage() {
  const { token } = theme.useToken();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const canRecord = usePermission('PAYMENT.CREATE');
  const canApprove = usePermission('PAYMENT.APPROVE');
  const canEditP = usePermission('PAYMENT.EDIT');
  const canViewRetailers = usePermission('RETAILER.VIEW');
  // The server gates the export on PAYMENT.EXPORT, so the button must ask for the SAME permission —
  // checking a VIEW permission here would show a button that 403s.
  const canExport = usePermission('PAYMENT.EXPORT');

  // "By bank" collections view is finance-only (PAYMENT.APPROVE = Super Admin / Admin / Accounts).
  // Held in the URL (`?tab=bank`) so leaving for a retailer/staff page and pressing Back returns to the
  // tab you were on, not to "Payments".
  /*
   * CASH IS A PAYMENT MODE, NOT A WORKFLOW.
   *
   * The third tab used to be a parallel cash universe — a collection queue, hand-over declarations and
   * a verification desk, all sitting beside the ordinary payment register. It meant a rep who had been
   * handed some notes faced a four-step approval chain that a UPI payment never saw, and it let a
   * stored custody balance drift away from the payments and the bank it was supposed to describe.
   *
   * Cash now runs through the same register, the same approval and the same reconciliation as UPI. What
   * remains specific to cash is only its CONSEQUENCE — the money is with a person until it reaches a
   * bank account — and that is a derived figure shown on the staff's own money page, not a workflow.
   */
  const [view, setView] = useUrlTab('tab', ['list', 'bank'] as const, 'list');

  /*
   * REP CASH IS ITS OWN TAB, not a section under the bank register.
   *
   * It is a work queue - cash sitting with field staff, and hand-overs waiting to be verified. It used
   * to live at the bottom of "By bank", where it pushed the account statement the reader had just
   * opened off the screen, and drew two full-height empty tables on the common day when nobody is
   * holding anything. A queue belongs behind its own tab with a count on it: the work stays visible
   * without occupying the page that answers a different question.
   */
  const [exportSlot, setExportSlot] = useState<HTMLElement | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [status, setStatus] = useUrlString<string | undefined>('status', undefined);
  const [mode, setMode] = useUrlString<string | undefined>('mode', undefined);
  const [bankAccountId, setBankAccountId] = useUrlString<string | undefined>('bank', undefined);
  /* The two views share this one `bank` key, and the By-bank register spends it on its own words:
     'ALL' (every account) and 'NONE' (cash / unassigned). Only 'NONE' means anything to a received-in
     filter. So coming back from ?tab=bank&bank=ALL left this list filtered to a bank account literally
     named ALL — no rows, every tile zero — while the picker showed a bare "ALL" that reads like the
     absence of a filter. Here "every account" IS the absence of one. */
  const bankFilter = bankAccountId === 'ALL' ? undefined : bankAccountId;
  const [searchInput, setSearchInput] = useUrlString('q');
  const search = useDebounce(searchInput, 400);
  const [retailerId, setRetailerId] = useUrlString<string | undefined>('retailer', undefined);
  const [range, setRange] = useUrlRangeOrNull('range');
  const [recordOpen, setRecordOpen] = useState(false);
  const [editFor, setEditFor] = useState<EditablePayment | null>(null);
  const [reviewFor, setReviewFor] = useState<any | null>(null);
  const { sortState, onSortChange, sort } = useServerSort();

  const params = useMemo(() => {
    const p: Record<string, any> = {};
    if (status) p.status = status;
    if (mode) p.mode = mode;
    if (bankFilter) p.bankAccountId = bankFilter;
    if (search.trim()) p.search = search.trim();
    if (retailerId) p.retailerId = retailerId;
    if (range?.[0] && range?.[1]) { p.from = range[0].format('YYYY-MM-DD'); p.to = range[1].format('YYYY-MM-DD'); }
    if (sort) p.sort = sort;
    return p;
  }, [status, mode, bankFilter, search, retailerId, range, sort]);

  const { data, isLoading } = useQuery({
    queryKey: ['payments', params],
    queryFn: () => request<any[]>({ url: '/payments', params }),
    placeholderData: keepPreviousData,
  });
  const rows = data ?? [];
  /*
   * A deep-link names the payment it meant (?focus=…). This list has no expandable row, so for an
   * APPROVER the honest equivalent of "open it" is the review dialog they were heading for anyway,
   * rather than leaving them to find one row among hundreds. The same link reaches the rep whose
   * collection was turned down (their My Work row): that dialog is a decision they cannot take, so
   * for them the row is simply scrolled to and highlighted.
   */
  /* Only while the COLLECTIONS list is the view. The bank register is a different table on the same
     route with its own `?focus=` (a deposit awaiting verification), and this hook runs whether or not
     its table is rendered — so on `?tab=bank` it consumed the param, found no payment with that id
     and dropped it, leaving the register with nothing to open. */
  const focus = useFocusRow(view === 'bank' ? undefined : rows, {
    match: ['id', 'paymentNo'],
    onFocus: canApprove ? (r) => setReviewFor(r) : undefined,
  });

  // KPI tiles reflect the FULL status breakdown for the current (non-status) filters, so clicking a status
  // tile filters the TABLE without zeroing the other tiles. Status (+ sort, irrelevant to totals) are dropped.
  const kpiParams = useMemo(() => { const { status: _s, sort: _so, ...rest } = params; return rest; }, [params]);
  const { data: kpiData } = useQuery({
    queryKey: ['payments-kpi', kpiParams],
    queryFn: () => request<any[]>({ url: '/payments', params: kpiParams }),
    placeholderData: keepPreviousData,
  });
  const kpiRows = kpiData ?? [];

  // The Retailer filter searches the server — a URL-selected dealer outside any page is fetched for its label.
  const retailerPick = useRetailerSearch({ key: 'payment-retailer-filter', enabled: canViewRetailers, selected: retailerId });
  const { data: bankAccounts } = useQuery({
    queryKey: ['bank-account-options'],
    queryFn: () => request<any[]>({ url: '/company/bank-accounts/options' }),
  });
  const bankFilterOpts = (Array.isArray(bankAccounts) ? bankAccounts : ((bankAccounts as any)?.data ?? []))
    .map((b: any) => ({ value: String(b.id), label: bankAcctLabel(b) }));

  const kpis = useMemo(() => {
    let pendingAmt = 0, pendingCnt = 0;
    let moneyAmt = 0, moneyCnt = 0, creditAmt = 0, creditCnt = 0;
    let rejectedCnt = 0, rejectedAmt = 0;
    const modes = new Set<string>();
    for (const p of kpiRows) {
      const a = Number(p.amount || 0);
      if (p.approvalStatus === 'PENDING') { pendingAmt += a; pendingCnt++; }
      else if (p.approvalStatus === 'APPROVED') {
        /* Approved money is two different things. A credit APPLICATION settles an order out of the
           retailer's existing account credit — nothing arrived in any bank — so it belongs to its
           own figure, not folded into (or silently subtracted from) money actually collected. Each
           half is counted here exactly as the server filters it, so the tile and the rows it opens
           can never disagree. */
        if (p.fromCredit) { creditAmt += a; creditCnt++; }
        else { moneyAmt += a; moneyCnt++; if (p.mode) modes.add(String(p.mode)); }
      } else if (p.approvalStatus === 'REJECTED') { rejectedCnt++; rejectedAmt += a; }
    }
    return {
      count: kpiRows.length, pendingAmt, pendingCnt,
      moneyAmt, moneyCnt, creditAmt, creditCnt, rejectedCnt, rejectedAmt,
      // The modes actually present, so the hint describes THIS data rather than listing every mode
      // the system supports.
      modes: [...modes].slice(0, 3).join(' · '),
    };
  }, [kpiRows]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['payments'] });
    qc.invalidateQueries({ queryKey: ['orders'] });
    qc.invalidateQueries({ queryKey: ['retailer-ledger'] });
    qc.invalidateQueries({ queryKey: ['badges'] });
  };

  /*
   * A receipt is a PICTURE. A chain-link icon told the reader a file exists and nothing about
   * whether it is the RIGHT file — checking meant opening a tab per row. The shared attachment cell
   * shows the thumbnail, badges "+N" when there are more, and opens the full-size viewer in place,
   * which is what orders, farmer KYC and the advance register already do.
   */
  const proofCell = (p: any) => {
    const files: string[] = (p.attachmentUrls?.length ? p.attachmentUrls : (p.attachmentUrl ? [p.attachmentUrl] : []));
    return (
      <span onClick={(e) => e.stopPropagation()}>
        <AttachmentCell files={files} label={`Proof · ${p.paymentNo}`} size={34} />
      </span>
    );
  };

  const columns: DataTableColumn<any>[] = [
    { key: 'paymentNo', title: 'Payment No', dataIndex: 'paymentNo', sortable: true, alwaysVisible: true, width: 150 },
    {
      /*
       * THE DATE IS A DAY; THE TIME IS THE RECORDING.
       *
       * `payments.payment_date` is a DATE column — the value date somebody typed — so it has no time
       * of its own, and formatting one out of it prints "12:00 AM" on every row. The time that does
       * exist is `created_at`: when the collection was actually entered. So the second line says the
       * time AND says which event it belongs to, rather than letting the reader assume it is a
       * clock reading of the date above it.
       *
       * Nearly every payment is recorded on the day it is dated. When the two differ (a back-dated
       * collection) the day comes with the time — "recorded 09 Sep, 02:02 PM" — because a bare time
       * under a different date is a false statement.
       */
      key: 'paymentDate', title: 'Date', dataIndex: 'paymentDate', sortable: true, width: 132,
      render: (v: any, p: any) => {
        const sameDay = !!p.createdAt && toIstDateString(p.createdAt) === String(v ?? '').slice(0, 10);
        return (
          <div className="min-w-0">
            <div className="whitespace-nowrap">{formatIstDate(v)}</div>
            {p.createdAt && (
              <div className="type-caption text-subtle" title={`Recorded ${formatIst(p.createdAt)}`}>
                recorded {sameDay ? formatIstTime(p.createdAt) : formatIst(p.createdAt, 'DD MMM, hh:mm A')}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'retailer', title: 'Retailer', alwaysVisible: true, width: 260,
      // Name and dealer code on ONE line: both are identity, and the code is what this desk quotes
      // against a bank line. 200px could not hold the pair without eating the name, so the column
      // carries 260 rather than the code keeping a line of its own.
      render: (_: any, p: any) => (p.retailerName
        ? (
          <div className="flex min-w-0 items-baseline gap-1.5">
            <span className="truncate" style={{ fontWeight: 600 }}><RetailerLink id={p.retailerId}>{p.retailerName}</RetailerLink></span>
            {p.retailerCode && <span className="shrink-0" style={{ fontSize: 12, color: token.colorTextTertiary }}>{p.retailerCode}</span>}
          </div>
        )
        : <span style={{ color: token.colorTextTertiary }}>#{p.retailerId}</span>),
    },
    {
      /* The order AND what this payment does to it. The second line costs no row height — the
         Retailer column beside it is already two lines — and it answers the question the reader
         actually has: is this order settled now, or is money still outstanding on it? */
      key: 'orderNo', title: 'Order', dataIndex: 'orderNo', width: 176,
      render: (v: any, p: any) => {
        if (!v) return <span style={{ color: token.colorTextTertiary }}>On-account</span>;
        const cov = orderCoverage(p);
        return (
          <div className="min-w-0">
            <Tooltip label="Open order"><Tag tone="blue" style={{ cursor: 'pointer', margin: 0 }} onClick={(e) => { e.stopPropagation(); navigate(`/orders/${p.orderId}`); }}>{v}</Tag></Tooltip>
            {cov && (
              <div className={`mt-0.5 truncate type-caption ${cov.tone === 'done' ? 'text-success-600 dark:text-success-500' : 'text-subtle'}`} title={cov.text}>
                {cov.text}
              </div>
            )}
          </div>
        );
      },
    },
    { key: 'mode', title: 'Mode', dataIndex: 'mode', width: 100, render: (v: string, p: any) => p.fromCredit ? <Tag tone="green">Credit</Tag> : <Tooltip label={p.referenceNo || p.utr || ''}><Tag>{v}</Tag></Tooltip> },
    {
      key: 'receivedBank', title: 'Received in', width: 210,
      render: (_: any, p: any) => p.receivedBank
        ? <Tooltip label={[p.receivedBank.label, p.receivedBank.bankName, p.receivedBank.last4 ? `A/c ··${p.receivedBank.last4}` : ''].filter(Boolean).join(' · ')}>
            <span className="block truncate whitespace-nowrap">{(p.receivedBank.bankName || p.receivedBank.label || 'Bank')}{p.receivedBank.last4 ? ` ··${p.receivedBank.last4}` : ''}</span>
          </Tooltip>
        : <span className="text-subtle">—</span>,
    },
    { key: 'amount', title: 'Amount', dataIndex: 'amount', sortable: true, width: 130, render: (v: any) => <b className="whitespace-nowrap">{inr2(v)}</b> },
    {
      key: 'approvalStatus', title: 'Status', dataIndex: 'approvalStatus', sortable: true, width: 120,
      render: (s: string, p: any) => {
        const m = APPROVAL[s] ?? { color: 'neutral', label: s };
        const tag = <Tag tone={m.color as any}>{m.label}</Tag>;
        return s === 'REJECTED' && p.rejectedReason ? <Tooltip label={p.rejectedReason}>{tag}</Tooltip> : tag;
      },
    },
    {
      key: 'submittedBy', title: 'Submitted by', width: 190,
      render: (_: any, p: any) => {
        const isRetailer = p.submittedByRoleCode === 'RETAILER' || p.submittedByRole === 'Retailer';
        if (isRetailer) {
          // Retailer self-submitted → the RETAILER (company) name, linked to its page (not the contact-person login name).
          const label = p.retailerName || p.submittedByName;
          if (!label) return '—';
          return (
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-1.5">
                <span className="truncate" title={label}><RetailerLink id={p.retailerId}>{label}</RetailerLink></span>
                <Tag tone="green" style={{ margin: 0 }} className="shrink-0">Retailer</Tag>
              </div>
              <div className="mt-0.5 truncate type-caption text-subtle">paid from their own login</div>
            </div>
          );
        }
        // Staff → their name, linked per role/permissions (USER.VIEW gated; never a Super Admin).
        const name = p.submittedByName || p.recordedByName;
        if (!name) return '—';
        // Prefer the role CODE; fall back to the "Super Admin" label so a SA is never linked even before
        // the backend serves submittedByRoleCode.
        const roleCode = p.submittedByRoleCode ?? (p.submittedByRole === 'Super Admin' ? 'SUPER_ADMIN' : undefined);
        const kind = submissionKind(p);
        return (
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-1.5">
              <span className="truncate" title={name}><UserLink role={roleCode}>{name}</UserLink></span>
              {p.submittedByRole && <Tag tone="blue" style={{ margin: 0 }} className="shrink-0">{p.submittedByRole}</Tag>}
            </div>
            {/* A rep collecting in the field and a clerk recording at a desk are different events.
                Both used to render as a name beside a role pill, leaving the reader to infer which
                had happened. The second line costs no row height — the Retailer cell beside it is
                already two lines. */}
            {kind && <div className="mt-0.5 truncate type-caption text-subtle">{kind}</div>}
          </div>
        );
      },
    },
    { key: 'proof', title: 'Proof', width: 88, align: 'center', render: (_: any, p: any) => proofCell(p) },
    {
      key: 'actions', title: '', align: 'right', width: 130, alwaysVisible: true,
      render: (_: any, p: any) => (
        <Space wrap size={4} className="justify-end" onClick={(e) => e.stopPropagation()}>
          {p.approvalStatus === 'PENDING' && canApprove && (
            <Tooltip label="Review this payment"><Button size="sm" variant="primary" aria-label="Review this payment" icon={<Eye size={16} />} onClick={() => setReviewFor(p)} /></Tooltip>
          )}
          {p.approvalStatus === 'APPROVED' && canEditP && (
            <Tooltip label="Edit payment"><Button variant="text" size="sm" icon={<Pencil size={16} />} onClick={() => setEditFor(p)} aria-label="Edit payment" /></Tooltip>
          )}
          {/*
            * Delete is gone, deliberately.
            *
            * It HARD-deleted the row: an approved payment's ledger credit was reversed and the
            * payment itself ceased to exist, leaving nothing to audit and nothing to explain the
            * hole. It also silently orphaned everything hanging off that payment — a rep's cash
            * custody outlived the payment that created it, so somebody was accountable for money
            * whose origin had been erased.
            *
            * Both real needs are already served, and both keep the record: a PENDING submission is
            * REJECTED with a reason, and an APPROVED payment is REFUNDED with proof. Neither
            * pretends the money was never there.
            */}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Typography.Title level={4} style={{ margin: 0 }}>Payments</Typography.Title>
        {canApprove && (
          <Segmented
            value={view}
            onChange={(v) => setView(v as 'list' | 'bank')}
            options={[
              // "Incoming": money coming IN from retailers. Staff advances and expense reimbursements
              // are money going OUT to people and live on their own page — naming the direction here stops
              // anyone looking for them in the wrong register.
              { label: 'Incoming payments', value: 'list', icon: <Wallet /> },
              { label: 'By bank', value: 'bank', icon: <Landmark /> },
            ]}
          />
        )}
        {view === 'list' && (
          /* What this list is — and, just as importantly, what it is NOT. Staff advances and expense
             reimbursements are money paid OUT to a person, not received from a retailer, so they can
             never appear here; people look for them and conclude something is missing. Kept as a
             one-line pill on the title row (the detail lives in the popover) so it reads as a caption
             rather than a full-bleed alert bar. */
          <Popover
            placement="bottomLeft"
            overlayClassName="max-w-sm"
            content={
              <div className="space-y-2">
                <p>Collections against orders, whatever the mode — cash, UPI, cheque, bank transfer or applied credit.</p>
                <p>
                  <b className="text-content">Not in this list:</b> staff advances and expense reimbursements. Those are
                  money paid <i>out</i> to a person, not retailer collections.
                </p>
                {canApprove && (
                  <button
                    type="button"
                    className="mt-1 flex w-full items-center justify-center gap-1.5 rounded-control bg-brand-50 px-3 py-2 type-label-strong text-brand-700 transition-colors hover:bg-brand-100 dark:bg-brand-500/10 dark:text-brand-300 dark:hover:bg-brand-500/20"
                    onClick={() => navigate('/billing/payments?tab=bank&bank=ALL')}
                  >
                    <Landmark size={14} className="shrink-0" />
                    See every money movement
                    <ArrowRight size={14} className="shrink-0" />
                  </button>
                )}
              </div>
            }
            title="Money received from retailers"
          >
            <button
              type="button"
              className="inline-flex max-w-full items-center gap-2 rounded-full border border-line bg-surface-2 py-1.5 pl-3 pr-3.5 type-body text-muted transition-colors hover:border-brand-300 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
            >
              <Info size={16} className="shrink-0 text-brand-600" />
              <span className="truncate">Retailer collections only — see every money movement</span>
            </button>
          </Popover>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* Slot the By-bank view portals its summary-export button into. A ref CALLBACK into state
              (not a plain ref) so the child re-renders once the node is actually mounted — read during
              the first render a ref would still be null and the portal would never appear. */}
          <span ref={setExportSlot} className="contents" />
          {view === 'list' && canExport && (
            <Button size="sm" icon={<Download size={16} />} onClick={() => setExportOpen(true)}>Export</Button>
          )}
          {canRecord && (<Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => setRecordOpen(true)}>Record Payment</Button>)}
        </div>
      </div>

      {/* `view` comes from the URL, so hiding the toggle is NOT enough: /billing/payments?tab=bank would
          render the bank view for anyone holding PAYMENT.VIEW (a Sales Person does). The collections
          data itself is safe — those endpoints need PAYMENT.APPROVE — but the bank PICKER reads
          /company/bank-accounts/options, which also accepts ORDER.PAY, so a rep would have seen the
          company's bank accounts. Gate the render on the same permission as the toggle. */}
      {view === 'bank' && canApprove ? (
        <BankCollectionsView headerSlot={exportSlot} />
      ) : (
      <>
      {/*
        * Every tile is a filter, and every tile's number is EXACTLY the rows it opens.
        *
        * "Approved (cash)" was neither. It read as a payment mode — beside a table of UPI, NEFT and
        * cheque rows, "cash" is the word for one of them — while it actually meant "money, as
        * opposed to credit applied". Worse, the figure excluded credit applications but the click
        * filtered to plain APPROVED, which includes them: the total on the tile could not be found
        * in the list it opened. It is now two tiles, each naming what it is and each tying to its
        * own rows.
        */}
      <Row align="stretch" gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={8} xl={4}><Kpi label="Pending approval" value={inr(kpis.pendingAmt)}
          hint={kpis.pendingCnt ? `${kpis.pendingCnt} to review` : 'nothing waiting'}
          tone="gold" icon={<Clock size={18} />}
          active={status === 'PENDING'} onClick={() => setStatus(status === 'PENDING' ? undefined : 'PENDING')} /></Col>
        <Col xs={12} md={8} xl={5}><Kpi label="Money received" value={inr(kpis.moneyAmt)}
          hint={kpis.moneyCnt ? `${kpis.moneyCnt} payments${kpis.modes ? ` · ${kpis.modes}` : ''}` : 'nothing collected'}
          tone="success" icon={<CheckCircle2 size={18} />}
          active={status === 'MONEY_IN'} onClick={() => setStatus(status === 'MONEY_IN' ? undefined : 'MONEY_IN')} /></Col>
        <Col xs={12} md={8} xl={5}><Kpi label="Settled from credit" value={inr(kpis.creditAmt)}
          hint={kpis.creditCnt ? `${kpis.creditCnt} settled from account credit — no money in` : 'none settled from credit'}
          tone="sky" icon={<Wallet size={18} />}
          active={status === 'CREDIT_APPLIED'} onClick={() => setStatus(status === 'CREDIT_APPLIED' ? undefined : 'CREDIT_APPLIED')} /></Col>
        <Col xs={12} md={12} xl={5}><Kpi label="Rejected" value={inr(kpis.rejectedAmt)}
          hint={kpis.rejectedCnt ? `${kpis.rejectedCnt} turned down` : 'none turned down'}
          tone={kpis.rejectedCnt ? 'danger' : 'neutral'} icon={<XCircle size={18} />}
          active={status === 'REJECTED'} onClick={() => setStatus(status === 'REJECTED' ? undefined : 'REJECTED')} /></Col>
        <Col xs={12} md={12} xl={5}><Kpi label="All payments" value={String(kpis.count)}
          hint={status ? 'clear the filter' : 'every status'} tone="brand" icon={<Landmark size={18} />}
          active={!status} onClick={() => setStatus(undefined)} /></Col>
      </Row>

      <Card>
        <DataTable
          storageKey="payments-list"
          rowKey="id"
          loading={isLoading}
          dataSource={rows}
          rowClassName={focus.rowClassName}
          // The row opens the payment. A pending one opens as the approver's review (same as the eye
          // button); anything else opens the same dialog read-only. Links, the proof thumbnail and the
          // action buttons stop propagation, so they still do their own thing.
          onRow={(p: any) => ({ onClick: () => setReviewFor(p) })}
          columns={columns}
          sortState={sortState}
          onSortChange={onSortChange}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          toolbarLeft={
            <Space wrap>
              <Input placeholder="Payment no" style={{ width: 150 }} value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
              <DatePicker.RangePicker disabledDate={disableFuture} value={range} onChange={(r) => setRange(r as [Dayjs, Dayjs] | null)} />
              {canViewRetailers && (
                <Select placeholder="Retailer" allowClear {...CUSTOMER_SELECT_PROPS} filterOption={false} onSearch={retailerPick.onSearch}
                  style={{ width: 200 }} value={retailerId} onChange={setRetailerId}
                  options={retailerPick.rows.map((r: any) => customerOption(r))} />
              )}
              <Select placeholder="Mode" allowClear style={{ width: 120 }} value={mode} onChange={setMode} options={MODES.map((m) => ({ value: m, label: m }))} />
              {bankFilterOpts.length > 0 && (
                <Select placeholder="Received in (bank)" allowClear showSearch optionFilterProp="label" style={{ width: 200 }}
                  value={bankFilter} onChange={setBankAccountId} options={bankFilterOpts} />
              )}
              {/* Includes the two the tiles set — a picker that cannot show the value it holds
                  prints the raw constant instead of a label. */}
              <Select placeholder="Status" allowClear style={{ width: 218 }} value={status} onChange={setStatus}
                options={[
                  ...Object.entries(APPROVAL).map(([v, m]) => ({ value: v, label: m.label })),
                  { value: 'MONEY_IN', label: 'Approved · money received' },
                  { value: 'CREDIT_APPLIED', label: 'Approved · from credit' },
                ]} />
            </Space>
          }
        />
      </Card>
      </>
      )}

      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Export collections"
        description="The payments you can see, with the filters this page is showing."
        baseUrl="/payments/export"
        baseParams={params}
        initialRange={range}
      />
      <PaymentFormModal open={recordOpen} onClose={() => setRecordOpen(false)} />
      <EditPaymentModal open={!!editFor} payment={editFor} onClose={() => setEditFor(null)} />
      <PaymentReviewModal open={!!reviewFor} payment={reviewFor} onClose={() => setReviewFor(null)} />
    </div>
  );
}

/**
 * The same KPI tile the rest of the app uses (ui/StatCard) — this page used to draw its own with
 * inline hex colours, so its numbers were thin and coloured while every other page's were bold and
 * legible. The only thing added here is the pressed state, because these tiles double as filters.
 */
function Kpi({ label, value, hint, tone, icon, onClick, active }: {
  label: string; value: React.ReactNode; hint?: string; tone: StatTone; icon: React.ReactNode; onClick?: () => void; active?: boolean;
}) {
  return (
    <StatCard
      label={label}
      value={value}
      hint={hint}
      tone={tone}
      icon={icon}
      onClick={onClick}
      active={active}
    />
  );
}
